/**
 * GSC CONTRACT: SCORING & CERTIFICATION
 */
import {
  RawDspMetrics,
  Biomechanics,
  GhostNoteData,
  ToneDetails,
  PeakDetail,
  TechnicalProof,
  CalibrationStatus
} from "./audio";
import type { EvidenceAtom, EvidenceStatus } from "./truth";


export type {
  RawDspMetrics,
  CalibrationStatus,
  PeakDetail,
  Biomechanics,
  GhostNoteData,
  ToneDetails,
  TechnicalProof
} from "./audio";

export enum AppState {
  IDLE = "IDLE",
  ANALYZING = "ANALYZING",
  RESULTS = "RESULTS"
}

export type MatchType = "FILE" | "ASSESSMENT" | "ARENA" | "LEAGUE";
export type AnalysisStage =
  | "IDLE"
  | "DECODING"
  | "CERTIFYING"
  | "DSP_WORKER"
  | "SCORING"
  | "IA_CORE"
  | "DONE";

export interface RemoteAIResult {
  advice: string;
  picking_analysis: string;
  harmonic_recommendation: string;
  model_id: string;
}

export interface AnalysisMetrics {
  rhythm: number;
  harmony: number;
  tone: number;
  cleanliness: number;
  articulation: number;
  nuance: number;
  synchronization: number;

  match_score: number;
  quality_score: number;

  posture: number | null;

  confidence: number;
  confidenceThreshold: number;

  accentuation: any;

  timingErrorMs: number;
  toleranceMs: number;
  bpm: number;

  debug?: RawDspMetrics;
  proof?: TechnicalProof;
}

export interface PerformanceIntegrityPack {
  origin: string;
  analysis_hash: string;
  capture_hash: string;
  reasons: string[];
  confidence: number;
  audio_source: string;
  engine_mode: string;
  filenames: { ref: string; stu: string };
}

export interface ScoreReason {
  ruleId: string;
  label: string;
  value: string | number;
  threshold: string | number;
  unit: string;
  impact: number;
  isCritical: boolean;
  measureIndex?: number;
}

/**
 * ✅ UNIQUE SOURCE OF TRUTH (V28+)
 * UI lit ce statut, ne l’invente jamais.
 */
export type IntegrityStatus = "PASS" | "FAIL" | "UNVERIFIABLE";

export interface SessionTruthReport {
  status: "OK" | "INVALID";
  integrity_status: IntegrityStatus;
  calibration_status: CalibrationStatus;
  integrity_message?: string;
  confidence: number;
  ux_message: string;

  score: number | null;
  match_score: number | null;

  score_reasons: ScoreReason[];
  tech_pack: PerformanceIntegrityPack;
}

export interface ChordValidation {
  measureIndex: number;
  expected: string;
  detected: string;
  score: number;
  isCorrect: boolean;
  confidence: number;
}

/* ------------------------------------------------------------------
   🧠 CONTRAT PÉDAGOGIQUE MOTEUR
------------------------------------------------------------------- */

export interface DiagnosticExplanation {
  signal_id: string;
  domain: string;
  title: string;
  what_is_measured: string;
  why_it_matters: string;
  severity: "CRITICAL" | "WARNING" | "SUCCESS";
}

export interface MotorPrescription {
  motor_action_id: string;
  domain: string;
  priority: number;
  title: string;
  instruction: string;
  steps: string[];
  duration_s: number;
  force_mode: boolean;
  recheck_signal_id: string;
}

export interface ActionableAdvice {
  domain: string;
  severity: "CRITICAL" | "WARNING" | "SUCCESS";
  title: string;
  instruction: string;
  drill_hint: string;
  icon: string;
  id?: string;

  // ✅ NEW (optionnel pour compat, mais requis pour affichage UI en NO-SIM)
  evidence_status?: EvidenceStatus;
  evidence?: EvidenceAtom[];   // minimum 1 si affiché
  derived_from?: string[];     // ex: ["session.metrics.debug.timingDeviationMs"]
}


export interface AICoachSummary {
  insight: string;
  focus: string;
  root_cause_analysis: string;
  learning_outcome: string;
  exercise: SmartExercise;
  protocol: TransformationProtocol;
  ui_hint: string;
}

export interface SmartExercise {
  id: string;
  name: string;
  rules: string[];
  duration: string;
  preset_grid?: string;
  preset_bpm?: number;
  preset_level?: number;
  title?: string;
  instruction?: string;
  root_cause?: string;
}

export interface TransformationProtocol {
  priority_focus_id: "TIMING" | "HARMONY" | "CLEANLINESS" | "POSTURE";
  remediation_steps: string[];
  transfer_test_instruction: string;
}

export type TutorialContext = "STUDIO" | "RESULTS" | "RECORDER";

export interface TutorialStep {
  targetId: string;
  title: string;
  content: string;
  position: "top" | "bottom" | "left" | "right";
}

export type OverlayType =
  | "NONE"
  | "HUB"
  | "TEACHER"
  | "STEM"
  | "GOD_MODE"
  | "TRUTH_PANEL"
  | "SKILL_TEST"
  | "SCORING_GUIDE";

export type HubTab = "SONGBOOK" | "INSTITUTION" | "ARENA" | "REWARDS";
export type HelpTab =
  | "MANUAL"
  | "IA"
  | "ARENA"
  | "LEAGUE"
  | "REWARDS"
  | "HISTORY"
  | "DIAG";
export type AccessibilityMode = "STANDARD" | "HIGH_CONTRAST" | "TEXT_ONLY";

export type SeverityMatrix = Record<number, LevelProfile>;

export interface LevelProfile {
  label: string;
  description: string;
  hardGate: { invalidSignal: boolean; minSignalDb: number; maxClipRatio: number };

  weights: {
    stability: number;
    timing: number;
    harmony: number;
    timbre: number;
    articulation: number;
    nuance: number;
    cleanliness: number;
    posture: number;
  };

  thresholds: { master: number; solid: number; ok: number; subdivisionsPerBeat?: number };
  adviceFocus: string[];

  timing: { toleranceMsBase: number; allowTrends: boolean };
  harmony: { minChordConfidence: number };

  cleanliness: {
    maxGhostsPerMin: number;
    ghostPenaltyWeight: number;
    ghostPenaltyWeightPerGhost: number;
    description: string;
  };

  articulation: { minTransientRatio: number; description: string };
  dynamics: { minRelativeRange: number };
  stability: { maxJitterMs: number };
}

export interface DifficultyLevel {
  id: number;
  name: string;
}

export interface TestResult {
  id: string;
  name: string;
  category: string;
  passed: boolean;
  value: string;
  expected: string;
  details: string;
}

export interface AlignedUrls {
  ref: string;
  stu: string;
}

/* ------------------------------------------------------------------
   ✅ ROOT REPORTS (Tabs + UI)
------------------------------------------------------------------- */

export interface RhythmReport {
  refEnvelope: number[];
  stuEnvelope: number[];

  // ✅ PeakDetail[] (pas number[])
  refPeaks: PeakDetail[];
  stuPeaks: PeakDetail[];

  // ✅ stabilité / consistance
  consistency: number;

  beatGrid?: any;
}

export interface HarmonyReport {
  progression: string[];
  gridValidation: ChordValidation[];
  chromaRef: number[][];
  chromaStu: number[][];
}

export interface ToneReport {
  details: ToneDetails;

  // ✅ fingerprint = number[] (cf contracts/audio.ts)
  mfccData: number[];
  mfccDataRef: number[];

  // ✅ ghosts = GhostNoteData[]
  ghostNotes: GhostNoteData[];
}

export interface AnalysisResult {
  app_version: string;
  coaching_level: number;
  match_type?: MatchType;

  alignedUrls?: AlignedUrls;
  rhythm?: RhythmReport;
  harmony?: HarmonyReport;
  tone?: ToneReport;

  biomechanics?: Biomechanics | null;

  session: {
    score: number | null;
    match_score: number | null;
    quality_score: number | null;
    rank: string;
    bpm: number;
    duration: number;
    lagMs: number;
    playbackRatio: number;
    firstOnset: number;
    metrics: AnalysisMetrics;
    isUnmeasurable: boolean;
    truth_report: SessionTruthReport;
    audio_quality_warning: string | null;
    biomechanics?: Biomechanics | null;
    certified: boolean;
  };

 feedback: {
  explanations: DiagnosticExplanation[];
  primaryPrescription?: MotorPrescription;

  diagnostics: ActionableAdvice[];
  advice: string[];
  exercises: string[];
  aiCoachSummary?: AICoachSummary;

  // ✅ NEW (NO-SIM narrative) — optional = zéro casse
  narrative?: any;

  // ✅ NEW (truth/proof) — optional = zéro casse
  truth_payload?: any;
  proof_bundle?: any;
};
  
  audit_trail: {
    audio_hash: string;
    analysis_hash: string;
    engine_version: string;
    scoring_ruleset_id: string;
    metrics_used: string[];
    fallback_used: boolean;
    local_seal: string;
    computed_at: string;
    invariants: any;
    remote_ai?: RemoteAIResult | null;
    remote_ai_status: "OK" | "UNAVAILABLE" | "PENDING";
  };

}
